import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Clock3, Video } from 'lucide-react';
import { getVehicleRecordings, type RecordingsAvailabilityDto } from '../../lib/api/endpoints';
import { ApiError } from '../../lib/api/client';
import { useHlsPlayer } from '../../hooks/useHlsPlayer';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import { CAMERA_LABEL_LABELS } from '../../utils/constants';
import { describeGaps } from '../../utils/recordingGaps';
import type { VehicleCamera } from '../../types/vehicle';

const MAX_PLAYLIST_MS = 60 * 60 * 1000;
const MAX_DISCOVERY_MS = 24 * MAX_PLAYLIST_MS;

interface HistoricalVideoPlayerProps {
  vehicleId: string;
  cameras: VehicleCamera[];
  initialCameraId?: string;
  /** #193 — ventana inicial (ISO) para pre-cargar el rango, p. ej. la del viaje. */
  initialFrom?: string;
  initialTo?: string;
  onClose: () => void;
}

interface Range {
  from: string;
  to: string;
}

function localInputValue(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return 'No tienes permiso para ver histórico de este vehículo.';
    if (err.status === 404) return 'No se encontró la cámara o el vehículo solicitado.';
    if (err.status === 400) return err.detail ?? 'El rango solicitado no es válido.';
    return err.detail ? `${err.title}: ${err.detail}` : err.title;
  }
  return err instanceof Error ? err.message : 'No se pudo consultar el histórico.';
}

function formatInstant(instant: string | null): string {
  if (!instant) return '—';
  return new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'short', timeStyle: 'medium',
  }).format(new Date(instant));
}

function toIso(value: string): string {
  return new Date(value).toISOString();
}

function timelinePercent(range: Range, instant: string): number {
  const start = new Date(range.from).getTime();
  const duration = new Date(range.to).getTime() - start;
  return duration <= 0 ? 0 : ((new Date(instant).getTime() - start) / duration) * 100;
}

// #198 — instante exacto dentro de un segmento según dónde se pulsó en la barra.
// Sustituye a la navegación Anterior/Siguiente: clicar al final de una grabación
// continua de más de 1h cae en el instante correcto y `selectInstant` carga la
// ventana de 1h que lo contiene. Sin esto, un clic en el span siempre caía en su
// inicio y no había forma (salvo el nav) de pasar de la primera hora.
function instantAtClick(el: HTMLElement, clientX: number, span: Range): string {
  const rect = el.getBoundingClientRect();
  const frac = rect.width > 0 ? Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)) : 0;
  const start = new Date(span.from).getTime();
  const end = new Date(span.to).getTime();
  return new Date(start + frac * (end - start)).toISOString();
}

// FE-18: el histórico se monta solo mientras está abierto; al ocultarlo,
// useHlsPlayer destruye el HLS y el <video> sin conservar URLs efímeras.
export function HistoricalVideoPlayer({ vehicleId, cameras, initialCameraId, initialFrom, initialTo, onClose }: HistoricalVideoPlayerProps) {
  const [cameraId, setCameraId] = useState(initialCameraId ?? cameras[0]?.id ?? '');
  const [fromInput, setFromInput] = useState(() =>
    localInputValue(initialFrom ? new Date(initialFrom) : new Date(Date.now() - MAX_PLAYLIST_MS)));
  const [toInput, setToInput] = useState(() =>
    localInputValue(initialTo ? new Date(initialTo) : new Date()));
  const [validationError, setValidationError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<RecordingsAvailabilityDto | null>(null);
  const [range, setRange] = useState<Range | null>(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [selectedInstant, setSelectedInstant] = useState<string | null>(null);
  const [windowIndex, setWindowIndex] = useState(0);
  const [playlistUrl, setPlaylistUrl] = useState<string | null>(null);
  const [loadingPlaylist, setLoadingPlaylist] = useState(false);
  const [playlistError, setPlaylistError] = useState<string | null>(null);
  // Un hueco terminal no conserva ni deja terminar de cargar el VOD previo.
  const [playbackDisabled, setPlaybackDisabled] = useState(false);

  const activeCamera = cameras.find(camera => camera.id === cameraId) ?? cameras[0];
  const windowCount = useMemo(() => {
    if (!range) return 0;
    return Math.max(1, Math.ceil((new Date(range.to).getTime() - new Date(range.from).getTime()) / MAX_PLAYLIST_MS));
  }, [range]);
  const playbackWindow = useMemo<Range | null>(() => {
    if (!range) return null;
    const rangeStart = new Date(range.from).getTime();
    const rangeEnd = new Date(range.to).getTime();
    const from = rangeStart + windowIndex * MAX_PLAYLIST_MS;
    return { from: new Date(from).toISOString(), to: new Date(Math.min(from + MAX_PLAYLIST_MS, rangeEnd)).toISOString() };
  }, [range, windowIndex]);

  useEffect(() => {
    if (!playbackWindow || !activeCamera || playbackDisabled) {
      setPlaylistUrl(null);
      setLoadingPlaylist(false);
      return undefined;
    }
    let cancelled = false;
    setLoadingPlaylist(true);
    setPlaylistError(null);
    setPlaylistUrl(null);

    getVehicleRecordings(vehicleId, activeCamera.id, playbackWindow)
      .then(result => {
        if (cancelled) return;
        // Una ventana sin spans devuelve 200 con playlist vacía válida. No la
        // presentamos como error ni intentamos reproducir un hueco.
        if (result.spans.length === 0 || !result.playlistUrl) {
          setPlaylistError('No hay video grabado en esta ventana.');
          return;
        }
        setPlaylistUrl(result.playlistUrl);
      })
      .catch(error => {
        if (!cancelled) setPlaylistError(describeError(error));
      })
      .finally(() => {
        if (!cancelled) setLoadingPlaylist(false);
      });

    return () => { cancelled = true; };
  }, [activeCamera, playbackDisabled, playbackWindow, vehicleId]);

  const { videoRef, error: playerError, isReconnecting, isSupported, playingProgramDateTime, seekToProgramDateTime } = useHlsPlayer(playlistUrl, {
    seekToProgramDateTime: selectedInstant,
  });

  const selectInstant = (instant: string) => {
    if (!range) return;
    setPlaybackDisabled(false);
    const index = Math.min(
      windowCount - 1,
      Math.max(0, Math.floor((new Date(instant).getTime() - new Date(range.from).getTime()) / MAX_PLAYLIST_MS)),
    );
    setSelectedInstant(instant);
    setWindowIndex(index);
    // Volver a pulsar el tramo en el que YA estás no cambia `selectedInstant`,
    // y el efecto de useHlsPlayer depende de ese valor: sin esta llamada el
    // segundo clic sobre un mismo tramo no hace nada y la línea de tiempo
    // parece muerta (medido en PAT001, 2026-09-15). Solo dentro de la ventana
    // actual: si el clic cambia de ventana la playlist se recarga y el seek lo
    // aplica LEVEL_LOADED sobre los fragmentos nuevos.
    if (index === windowIndex) seekToProgramDateTime(instant);
  };

  const loadHistory = async () => {
    if (!activeCamera) return;
    const from = new Date(fromInput);
    const to = new Date(toInput);
    const duration = to.getTime() - from.getTime();
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
      setValidationError('Elige una fecha y hora de inicio y de fin.');
      return;
    }
    if (duration <= 0) {
      setValidationError('La hora final debe ser posterior a la inicial.');
      return;
    }
    if (duration > MAX_DISCOVERY_MS) {
      setValidationError('El rango máximo para histórico es de 24 horas.');
      return;
    }

    const nextRange = { from: toIso(fromInput), to: toIso(toInput) };
    setValidationError(null);
    setLoadingAvailability(true);
    setAvailabilityError(null);
    setAvailability(null);
    setRange(null);
    setPlaylistUrl(null);
    setPlaybackDisabled(false);
    try {
      const result = await getVehicleRecordings(vehicleId, activeCamera.id, nextRange);
      setAvailability(result);
      setRange({ from: result.from, to: result.to });
      setWindowIndex(0);
      setSelectedInstant(result.spans[0]?.from ?? null);
    } catch (error) {
      setAvailabilityError(describeError(error));
    } finally {
      setLoadingAvailability(false);
    }
  };

  // #193 — cuando se abre con una ventana inicial (el tramo del viaje), busca
  // sola una vez, para que el expediente muestre el video sin un paso extra.
  // Si la ventana supera las 24 h, deja el formulario pre-cargado sin buscar
  // (loadHistory la rechazaría): el usuario la ajusta.
  const autoLoadedRef = useRef(false);
  useEffect(() => {
    if (autoLoadedRef.current || !initialFrom || !initialTo || !activeCamera) return;
    const duration = new Date(initialTo).getTime() - new Date(initialFrom).getTime();
    if (!(duration > 0 && duration <= MAX_DISCOVERY_MS)) return;
    autoLoadedRef.current = true;
    void loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCamera, initialFrom, initialTo]);

  const selectGap = (gapEnd: string) => {
    const nextSpan = availability?.spans.find(span => new Date(span.from) >= new Date(gapEnd));
    if (nextSpan) {
      selectInstant(nextSpan.from);
      return;
    }
    setSelectedInstant(null);
    setPlaybackDisabled(true);
    setPlaylistUrl(null);
    setPlaylistError('No queda video grabado después de este hueco.');
  };

  return (
    <div className="historical-video">
      <div className="historical-video__controls">
        <Select
          label="Cámara"
          value={activeCamera?.id ?? ''}
          onChange={event => {
            setCameraId(event.target.value);
            setAvailability(null);
            setRange(null);
            setPlaylistUrl(null);
            setPlaybackDisabled(false);
          }}
          options={cameras.map(camera => ({ value: camera.id, label: `${camera.name || CAMERA_LABEL_LABELS[camera.label]}${camera.enabled ? '' : ' (deshabilitada hoy)'}` }))}
        />
        <Input label="Desde (hora local)" type="datetime-local" value={fromInput} onChange={event => setFromInput(event.target.value)} />
        <Input label="Hasta (hora local)" type="datetime-local" value={toInput} onChange={event => setToInput(event.target.value)} />
        <Button size="sm" leftIcon={<Video size={14} />} onClick={() => void loadHistory()} isLoading={loadingAvailability}>
          Buscar histórico
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose}>Cerrar</Button>
      </div>
      {validationError && <p className="historical-video__error">{validationError}</p>}
      {availabilityError && <ErrorMessage message={availabilityError} />}

      {availability && range && (
        <>
          <div className="historical-video__timeline" aria-label="Línea de tiempo de grabaciones">
            {availability.spans.map(span => {
              const left = timelinePercent(range, span.from);
              const right = timelinePercent(range, span.to);
              return (
                <button
                  key={`span-${span.from}`} type="button" className="historical-video__span"
                  style={{ left: `${left}%`, width: `${Math.max(0.4, right - left)}%` }}
                  title={`Grabación: ${formatInstant(span.from)} a ${formatInstant(span.to)}`}
                  onClick={e => selectInstant(instantAtClick(e.currentTarget, e.clientX, span))}
                />
              );
            })}
            {availability.gaps.map(gap => {
              const left = timelinePercent(range, gap.from);
              const right = timelinePercent(range, gap.to);
              return (
                <button
                  key={`gap-${gap.from}`} type="button" className="historical-video__gap"
                  style={{ left: `${left}%`, width: `${right - left}%` }}
                  title={`Sin grabación: ${formatInstant(gap.from)} a ${formatInstant(gap.to)}`}
                  onClick={() => selectGap(gap.to)}
                >
                  {right - left > 12 ? 'Sin grabación' : ''}
                </button>
              );
            })}
          </div>
          <div className="historical-video__legend"><span><i className="historical-video__legend-recording" /> Grabación</span><span><i className="historical-video__legend-gap" /> Sin grabación</span>{describeGaps(availability.gaps) && <span className="historical-video__legend-summary">· {describeGaps(availability.gaps)}</span>}</div>

          {availability.spans.length === 0 ? (
            <p className="historical-video__empty">No hay video grabado en este rango.</p>
          ) : (
            <>
              {/* #198 — sin navegación Anterior/Siguiente: la barra verde es el
                  único control de posición. Clicar en cualquier punto de un
                  segmento carga la ventana de 1h que lo contiene (ver
                  `instantAtClick`). Las grabaciones de más de 1h son raras y se
                  recorren clicando más a la derecha en su franja. */}
              <div className="historical-video__player">
                <video ref={videoRef} controls style={{ display: playlistUrl && isSupported && !playerError ? 'block' : 'none' }} />
                {loadingPlaylist && <Spinner />}
                {!isSupported && playlistUrl && <ErrorMessage message="Este navegador no puede reproducir HLS autenticado." />}
                {(playlistError || playerError) && !loadingPlaylist && <ErrorMessage message={playlistError ?? playerError ?? ''} />}
                {isReconnecting && !playerError && (
                  <p className="historical-video__reconnecting" role="status"><Spinner size="sm" /> Reconectando con el archivo…</p>
                )}
              </div>
              <p className="historical-video__played-at"><Clock3 size={13} /> Hora del fragmento: {formatInstant(playingProgramDateTime)}</p>
            </>
          )}
        </>
      )}
    </div>
  );
}

function ErrorMessage({ message }: { message: string }) {
  return <p className="historical-video__error"><AlertTriangle size={15} /> {message}</p>;
}
